import { get, send, unwrap } from "./api.js";
import { CARD_RECORD_PAGE_SIZE } from "./paginationPageSizes.js";

export type ResourceKind = "file" | "link";
export type ResourcePresentationKind = "pdf" | "image" | "audio" | "video" | "file" | "link";
export type ResourceTargetType = "practice_todo" | "repertoire" | "goal";

export interface ResourceAttachment {
  targetType: ResourceTargetType;
  targetId: string;
  title: string;
}

export interface StudentResource {
  id: string;
  title: string;
  description: string | null;
  kind: ResourceKind;
  originalFilename: string | null;
  externalUrl: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  presentationKind: ResourcePresentationKind;
  familyVisible: boolean;
  createdAt: string;
  updatedAt: string;
  attachments?: ResourceAttachment[];
  usageCount?: number;
}

export interface ResourcePage {
  items: StudentResource[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

export interface ResourceTargets {
  practiceTodos: Array<{ id: string; title: string }>;
  repertoire: Array<{ id: string; title: string }>;
  goals: Array<{ id: string; title: string }>;
}

export interface FamilyPracticeResource {
  id: string;
  title: string;
  description: string | null;
  kind: "file" | "link" | "groove";
  presentationKind: ResourcePresentationKind | "groove";
  sizeBytes: number | null;
  sharedAt: string;
  groupId: string;
  groupLabel: string;
  href: string;
}

export interface FamilyPracticeResourcePage {
  studentId: string;
  items: FamilyPracticeResource[];
  page: number;
  pageSize: number;
  total: number;
  sharedTotal: number;
  pages: number;
}

export function getFamilyPracticeResources(
  studentId: string,
  page: number,
  search: string,
  kind: "all" | "files" | "grooves",
): Promise<FamilyPracticeResourcePage> {
  return get<FamilyPracticeResourcePage>(`/me/students/${encodeURIComponent(studentId)}/practice-resources`, {
    page: String(page), pageSize: String(CARD_RECORD_PAGE_SIZE), search, kind,
  });
}

function studentPath(studentId: string): string {
  return `/admin/students/${encodeURIComponent(studentId)}/resources`;
}

export function getStaffResources(studentId: string, page = 1): Promise<ResourcePage> {
  return get<ResourcePage>(studentPath(studentId), { page: String(page), pageSize: "20" });
}

export function getFamilyResources(studentId: string, page = 1): Promise<ResourcePage> {
  return get<ResourcePage>(`/me/students/${encodeURIComponent(studentId)}/resources`, {
    page: String(page),
    pageSize: "20",
  });
}

export function getResourceTargets(studentId: string): Promise<ResourceTargets> {
  return get<ResourceTargets>(`${studentPath(studentId)}/targets`);
}

export async function addResourceLink(
  studentId: string,
  input: { title: string; description?: string | null; url: string; familyVisible: boolean },
): Promise<StudentResource> {
  const result = await send<{ resource: StudentResource }>("POST", studentPath(studentId), input);
  return result.resource;
}

export async function addResourceFile(
  studentId: string,
  input: {
    file: File;
    title: string;
    description?: string | null;
    familyVisible: boolean;
  },
): Promise<StudentResource> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.title.trim()) form.append("title", input.title.trim());
  if (input.description?.trim()) form.append("description", input.description.trim());
  form.append("familyVisible", String(input.familyVisible));
  const result = await unwrap<{ resource: StudentResource }>(
    await fetch(`/api${studentPath(studentId)}/files`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: form,
    }),
  );
  return result.resource;
}

export async function updateResource(
  studentId: string,
  resourceId: string,
  input: {
    title?: string;
    description?: string | null;
    url?: string;
    familyVisible?: boolean;
  },
): Promise<StudentResource> {
  const result = await send<{ resource: StudentResource }>(
    "PATCH",
    `${studentPath(studentId)}/${encodeURIComponent(resourceId)}`,
    input,
  );
  return result.resource;
}

export function archiveResource(studentId: string, resourceId: string): Promise<void> {
  return send("DELETE", `${studentPath(studentId)}/${encodeURIComponent(resourceId)}`);
}

export function attachResource(
  studentId: string,
  resourceId: string,
  targetType: ResourceTargetType,
  targetId: string,
): Promise<void> {
  return send("POST", `${studentPath(studentId)}/${encodeURIComponent(resourceId)}/attach`, {
    targetType,
    targetId,
  });
}

export function detachResource(
  studentId: string,
  resourceId: string,
  targetType: ResourceTargetType,
  targetId: string,
): Promise<void> {
  return send(
    "DELETE",
    `${studentPath(studentId)}/${encodeURIComponent(resourceId)}/attach/${targetType}/${encodeURIComponent(targetId)}`,
  );
}

export function staffResourceOpenPath(studentId: string, resourceId: string): string {
  return `/api${studentPath(studentId)}/${encodeURIComponent(resourceId)}/open`;
}

export function familyResourceOpenPath(studentId: string, resourceId: string): string {
  return `/api/me/students/${encodeURIComponent(studentId)}/resources/${encodeURIComponent(resourceId)}/open`;
}
